import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'
import { useCallback } from 'react'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import useSessionStore from '@/stores/useSessionStore'

import { OrderConnection, ordersFragment } from './useUserApi'

/**
 * Orders per fetched page. Each page must stay bounded: an unbounded query
 * 504s at the gateway for heavy accounts (thousands of orders), which used to
 * empty the whole account page (GSE-11860). 25 = five pages of the order
 * history's client-side pagination.
 */
export const ORDERS_PAGE_SIZE = 25

/**
 * Cursor-paginated user order history. Fetches ORDERS_PAGE_SIZE orders at a
 * time via the connection's Relay `after` cursor; `fetchNextPage` pulls the
 * next batch until `hasNextPage` is false.
 */
export const useUserOrdersApi = (): UseInfiniteQueryResult<
  InfiniteData<OrderConnection>,
  Error
> => {
  const client = useGraphQLClient()
  const userId = useSessionStore((state) => state.userId)

  const query = gql`
    query GetUserOrders($first: Int!, $after: String) {
      user {
        id
        orders(first: $first, after: $after) {
          ...orders
        }
      }
    }
    ${ordersFragment}
  `

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | null }): Promise<OrderConnection> =>
      client
        .request<{ user: { orders: OrderConnection } }>(query, {
          first: ORDERS_PAGE_SIZE,
          after: pageParam,
        })
        .then(({ user }) => user.orders),
    [client, query],
  )

  return useInfiniteQuery({
    enabled: !!userId,
    queryKey: ['user-orders', userId],
    queryFn,
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.pageInfo.orderHasNextPage ? lastPage.pageInfo.orderEndCursor : undefined,
  })
}
